import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useTimer } from 'react-timer-hook'
import { useMediaQuery } from 'usehooks-ts'

import { useAuthBySmsCode, useRequestNewSms } from '@entities/User'

import useProfileCustomerModal from '@features/auth/hooks/useProfileCustomerModal'
import { useAuth } from '@features/auth/provider'
import LoginOrRegister from '@features/auth/ui/form/login-or-register/LoginOrRegister'
import styles from '@features/auth/ui/form/styles.module.scss'
import { useErrorInfoModal, useModalStore } from '@features/modal'

import { useAnalytics } from '@shared/lib/analytics'
import Button from '@shared/ui/button'
import ErrorMessage from '@shared/ui/error-message'
import InputOTP from '@shared/ui/input-otp'




interface SmsCodeConfirmProps {
  numberPhone: string;
  id: string;
  timeout: number;
  onCloseCallback?: () => void;
}

export default function SmsCodeConfirm (props: SmsCodeConfirmProps) {
  const { numberPhone, timeout, id, onCloseCallback } = props
  
  const { openErrorInfoModal } = useErrorInfoModal()
  const { openProfileCustomerModal } = useProfileCustomerModal()
  const { openModal, closeModal } = useModalStore()
  
  const { refetchUser } = useAuth()
  const { login, signUp } = useAnalytics()
  
  const [ code, setCode ] = useState<string>('')
  const [ error, setError ] = useState<boolean>(false)

  const expiryTimestamp = new Date()
  expiryTimestamp.setSeconds(expiryTimestamp.getSeconds() + timeout)

  const { seconds, minutes, restart, isRunning } = useTimer({
    expiryTimestamp,
  })

  const isTablet = useMediaQuery('(max-width: 991px)')
  const { t: tu } = useTranslation('user')
  const { t: ta } = useTranslation('auth')
  const { t: tc } = useTranslation('common')
  const { t: te } = useTranslation('errors')

  const [ authBySmsCode ] = useAuthBySmsCode()
  const [ requestNewSms ] = useRequestNewSms()

  useEffect(() => {
    const firstInput = document.querySelector<HTMLInputElement>('input[name="sms"]')
    if (firstInput) {
      firstInput.focus()
    }
  }, [])

  const handleChange = () => {
    closeModal('smsCodeConfirm', true)
    openModal('loginOrRegister', <LoginOrRegister />, {
      title: ta('login.title'),
      onClose: onCloseCallback,
    })
  }

  const handleSubmit = async () => {
    try {
      const result = await authBySmsCode({
        variables: {
          smsCode: code,
          id
        }
      })

      if (result.data?.authBySmsCode?.status == 1) {
        closeModal('smsCodeConfirm', true)
      }

      if (!result.data?.authBySmsCode) {
        setError(true)

        throw new Error('authBySmsCode function error')
      }

      await refetchUser()
      
      // Проверяем требуется ли регистрация после обновления данных
      const data = await refetchUser()
      const isRequireRegistration = data?.requireRegistration

      // Track analytics
      if (isRequireRegistration) {
        signUp('phone')
      }
      else {
        login('phone')
      }

      if (isRequireRegistration) {
        openProfileCustomerModal(() => {
          if (onCloseCallback) {
            onCloseCallback()
          }
        })
      }

      setError(false)
    }
    catch (error) {
      console.error(error)
      openErrorInfoModal({
        description: te('sms_code_confirm_error'),
      })
      setError(true)
    }
  }

  const handleReset = async () => {
    try {
      const { data } = await requestNewSms({
        variables: {
          id,
        },
      })

      const timeout = data?.requestNewSms.timeout as number

      const expiryTimestamp = new Date()
      expiryTimestamp.setSeconds(expiryTimestamp.getSeconds() + timeout)

      if (!data?.requestNewSms.sent) {
        throw new Error('handleReset function error')
      }

      restart(expiryTimestamp)
      setCode('')
      setError(false)
    }
    catch (error) {
      openErrorInfoModal({
        description: te('request_new_sms_error'),
      })
      console.error(error)
    }
  }

  return (
    <div className={styles.root}>
      <div className={styles.root__top}>
        {ta('sms.code.send')} <br /> {numberPhone}
        <Button size="small" theme="grey" onClick={handleChange}>
          {tc('change')}
        </Button>
      </div>

      <div className={styles['root__group']}>
        <InputOTP
          onChange={(val: string) => setCode(val)}
          hasError={!!error}
          value={code}
          autoSubmit
          onSubmit={handleSubmit}
        />

        {error && <ErrorMessage>{tc('invalid_code')}</ErrorMessage>}

        <div className={styles['root__group-button']}>
          <Button size={isTablet ? 'medium' : 'large'} onClick={handleSubmit}>
            {tu('button')}
          </Button>

          {!isRunning && (
            <Button
              size={isTablet ? 'medium' : 'large'}
              theme="secondary"
              onClick={handleReset}
            >
              {ta('code.resend')}
            </Button>
          )}
        </div>
      </div>
      {isRunning && (
        <div className={styles.root__bottom}>
          {ta('code.resendTimer')} {minutes}:
          {seconds < 10 ? `0${seconds}` : seconds}
        </div>
      )}
    </div>
  )
}
